import type { AxiosDefaults, AxiosHeaderValue, AxiosInstance, HeadersDefaults } from "axios";
import type { Page } from "@zhihaoscm/entity";
import type { CustomAxiosRequestConfig } from "@/api";
import { confirmable } from "@/utils/decorator/confirmable";

/**
 * 基础请求服务
 * E entity类型
 * V Vo类型
 * P pagingConditions类型
 * F entityForm类型 或等同于entity类型
 */
export class BaseService<E, V, P, F> {
    protected readonly service: AxiosInstance;

    protected readonly path: string;

    constructor(path: string, service: AxiosInstance, config?: Partial<CustomAxiosRequestConfig>) {
        this.service = service;
        this.path = path;
        this.service.defaults.baseURL = `${this.service.defaults.baseURL}`;
        if (config)
            this.service.defaults = { ...this.service.defaults, ...config } as unknown as Omit<
                AxiosDefaults,
                "headers"
            > & {
                headers: HeadersDefaults & {
                    [key: string]: AxiosHeaderValue;
                };
            };
    }

    /**
     * 主页数据
     */
    public async paging(params: P): Promise<Page<V>> {
        const { data } = await this.service.get(`${this.path}/paging`, { params });
        return data;
    }

    /**
     * 下拉选框列表数据
     */
    public async selector(): Promise<Array<E>> {
        const { data } = await this.service.get(`${this.path}/selector`);
        return data;
    }

    /**
     * 根据ID查询
     * @param id
     */
    public async get(id: number | string): Promise<E> {
        const { data } = await this.service.get(`${this.path}/${id}`);
        return data;
    }

    /**
     * 根据ID查询
     * @param id
     */
    public async getInfo(id: number | string): Promise<V> {
        const { data } = await this.service.get(`${this.path}/${id}`);
        return data;
    }

    /**
     * 根据ID查询
     * @param id
     */
    public async getVo(id: number | string): Promise<V> {
        const { data } = await this.service.get(`${this.path}/vo/${id}`);
        return data;
    }

    /**
     * 创建实例
     * @param form
     */
    public async create(form: F): Promise<E> {
        const payload = { ...form };
        for (const key in payload) {
            const value = payload[key];
            if (value === undefined || value === null || value === "") {
                delete payload[key];
            }
        }
        const { data } = await this.service.post(this.path, payload);
        return data;
    }

    /**
     * 更新实例
     * @param id
     * @param form
     */
    public async update(id: number | string, form: F) {
        const { data } = await this.service.put(`${this.path}/${id}`, form);
        return data;
    }

    /**
     * 删除实例
     * @param id
     */
    public async delete(id: number | string) {
        const { data } = await this.service.delete(`${this.path}/${id}`);
        return data;
    }
}

/**
 * Vite 当前不会转换旧 Vue 服务中的装饰器语法，因此在类定义完成后保留同等的运行时装饰器应用。
 * @param propertyKey 需要确认拦截的方法名
 * @param message 与旧 Vue 服务一致的确认提示
 */
function applyConfirmable(propertyKey: "create" | "update" | "delete", message: string): void {
    const descriptor = Object.getOwnPropertyDescriptor(BaseService.prototype, propertyKey);
    if (!descriptor) {
        throw new Error(`BaseService method descriptor not found: ${propertyKey}`);
    }

    confirmable(message)(BaseService.prototype, propertyKey, descriptor);
    Object.defineProperty(BaseService.prototype, propertyKey, descriptor);
}

applyConfirmable("create", "是否保存");
applyConfirmable("update", "是否保存");
applyConfirmable("delete", "是否删除");
